import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable, race, timer } from 'rxjs';
import { filter, map, startWith, switchMapTo, take } from 'rxjs/operators';
import { PurchaseOrder } from '~lmo/models/purchaseOrders.model';
import { LmoConstantsService } from '~lmo/services/lmo-constants.service';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { idArrayToRecord } from '~utilities/idArrayToRecord';
import { recordToArray } from '~utilities/recordToArray';
import { Mesh, sortByPreferredOrder } from '~v2Models/mesh.model';

interface MeshWithPos extends Mesh {
  pos: PurchaseOrder[];
}

@Component({
  selector: 'sa-lmo-frac-fcpo-manage-pos',
  styleUrls: ['./lmo-frac-fcpo-manage-pos.component.scss'],
  templateUrl: './lmo-frac-fcpo-manage-pos.component.html',
})
export class LmoFracFcpoManagePosComponent implements OnInit {
  @ViewChild('confirmClose', { static: true }) private confirmClose: TemplateRef<any>;
  public activeNetworkRequest$ = new BehaviorSubject<boolean>(false);
  public searchForm = new FormControl(null);
  public filteredPos$: Observable<MeshWithPos[]>;

  constructor(
    private fcpoService: LmoFracFcpoService,
    private constantsService: LmoConstantsService,
    // private modalService: LmoFracModalService,
    private matDialog: MatDialog,
  ) {}

  public ngOnInit() {
    const groupedMeshes$: Observable<MeshWithPos[]> = combineLatest(
      this.constantsService.meshes$,
      this.fcpoService.purchaseOrders$,
    ).pipe(map(groupByMesh));
    this.filteredPos$ = combineLatest(
      race(
        groupedMeshes$.pipe(
          filter((meshes) => meshes && !!meshes.length),
          switchMapTo(groupedMeshes$),
        ),
        timer(1000).pipe(
          take(1),
          switchMapTo(groupedMeshes$),
        ),
      ),
      this.searchForm.valueChanges.pipe(startWith(null)),
    ).pipe(
      map(([meshesWithOrders, searchTerm]) => {
        if (!searchTerm || searchTerm === '') {
          return meshesWithOrders;
        }
        const filtered = meshesWithOrders
          .map((order) => {
            return Object.assign({}, order, {
              pos: order.pos.filter((pos) => pos.name.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0),
            });
          })
          .filter((order) => order.pos.length > 0);
        return filtered;
      }),
    );
  }

  public confirmClosePO(id: number) {
    const dialogRef = this.matDialog.open(this.confirmClose, {
      disableClose: true,
      maxWidth: '968px',
      minWidth: '300px',
      width: '30%',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.closePO(id);
      }
    });
  }

  public closePO(id: number) {
    this.activeNetworkRequest$.next(true);

    this.fcpoService.closePurchaseOrder$(id).subscribe((success) => {
      this.activeNetworkRequest$.next(false);
      // if (success) {
      //   this.modalService.closeHomeModal();
      // }
    });
  }
}

function groupByMesh([meshes, pos]: [Mesh[], PurchaseOrder[]]): MeshWithPos[] {
  if (!meshes || !meshes.length || !pos || !pos.length) {
    return;
  }

  const meshesRecord = idArrayToRecord(meshes);
  const meshesWithPosRecord: Record<number, MeshWithPos> = {};
  pos.forEach((po) => {
    if (!meshesWithPosRecord[po.meshId]) {
      meshesWithPosRecord[po.meshId] = {
        ...meshesRecord[po.meshId],
        pos: [],
      };
    }
    meshesWithPosRecord[po.meshId].pos.push(po);
  });

  const meshesWithPos = recordToArray(meshesWithPosRecord);
  const sortedMeshes = sortByPreferredOrder(meshesWithPos);
  sortedMeshes.forEach((mesh) => {
    mesh.pos.sort((a, b) => (a.name < b.name ? -1 : 1));
  });
  return sortedMeshes;
}
